<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div :class="panelMode ? 'p-3' :'container mt-4 mb-4'">
    <div class="mt-2" style="max-width:597px">
        <div class="text-muted">Tilbehørsbibliotek</div>
        <h3 style="padding-right: 60px">Rediger "{{ addOnTemplate.name }}"</h3>
        <div class="mt-2 text-muted">
          Endringer her oppdaterer valget i alle gruppene som bruker det.
        </div>

        <div class="mt-4">
          <span class="ingredient-title">Koble til produkt fra menyen (valgfritt)</span>
          <ItemPicker v-model="addOnTemplate.itemId" :items="items" :categories="categories" @change="onItemSelected"/>
          <div v-if="addOnTemplate.itemId" class="text-muted mt-1">
            Navn og pris hentes fra produktet, og holdes synkronisert. Når produktet er utsolgt, vises valget som utsolgt.
          </div>
        </div>

        <div class="mt-4">
          <span class="ingredient-title">Navn</span>
          <input class="form-control" type="text" v-model="addOnTemplate.name" :disabled="isLinkedToItem"/>
        </div>

        <div class="mt-4">
          <span class="ingredient-title">Pris ta med/takeaway</span>
        </div>
        <div class="row">
          <div class="col-sm-6">
            <input class="form-control" type="number" min="0" v-model="price" @input="updatePriceWithVat()" :disabled="isLinkedToItem"/>
            <div class="text-muted">(uten MVA)</div>
          </div>
          <div class="col-sm-6">
            <input class="form-control" type="number" min="0" v-model="priceWithVat" @input="updatePrice()" :disabled="isLinkedToItem"/>
            <div class="text-muted">(med MVA, {{ vat.takeOut }}%)</div>
          </div>
        </div>
        <div v-if="useFunctionality === 'POS' || useFunctionality === 'KIOSK_TAKEAWAY' || useFunctionality === 'ALL'">
          <div class="mt-3">
            <span class="ingredient-title">Pris spise her</span>
          </div>
          <div class="row">
            <div class="col-sm-6">
              <input class="form-control" type="number" min="0" v-model="priceDineIn" @input="updatePriceDineInWithVat()" :disabled="isLinkedToItem"/>
              <div class="text-muted">(uten MVA)</div>
            </div>
            <div class="col-sm-6">
              <input class="form-control" type="number" min="0" v-model="priceDineInWithVat" @input="updatePriceDineIn()" :disabled="isLinkedToItem"/>
              <div class="text-muted">(med MVA, {{ vat.dineIn }}%)</div>
            </div>
          </div>
          <div v-if="dineInPriceSuggestion" class="mt-1">
            <a href="#" class="bo_suggestion-btn" @click.prevent="applyDineInPriceSuggestion">
              Klikk for å bruke samme grunnpris som ta med: kr {{ dineInPriceSuggestion }} uten mva = kr {{ dineInPriceSuggestionWithVat }} med {{ vat.dineIn }}% mva
            </a>
          </div>
        </div>

        <div class="mt-4">
          <span class="ingredient-title">Brukes i disse gruppene</span>
          <div v-if="usedInAddOnGroupNames.length === 0" class="text-muted">Brukes ikke i noen grupper ennå.</div>
          <div v-else class="mt-1">
            <span v-for="addOnGroupName in usedInAddOnGroupNames" :key="addOnGroupName" class="bo_chip">{{ addOnGroupName }}</span>
          </div>
        </div>

        <div class="bo_hr mt-4">
          <hr/>
        </div>

        <div class="mb-5">
          <button v-if="activateSaveButton" type="button" class="bo_btn" @click="saveAddOnTemplate"> Lagre </button>
          <button v-else type="button" class="bo_btn" @click="saveAddOnTemplate" disabled="true"> Lagre </button>
        </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ItemPicker from "@/components/product/ItemPicker";
import MerchantUtil from "@/util/MerchantUtil";
import ProductService from "@/services/product/ProductService";
import ItemUtil from "@/util/ItemUtil";

export default {
  components: {LoadingSpinner, ItemPicker},
  props: {
    panelMode: { type: Boolean, default: false },
    vat: { type: Object, default: () => ({}) },
    addOnTemplateIdProp: { type: String, default: '' },
    //Data already loaded by the products page, makes the panel open without any requests
    addOnTemplateProp: { type: Object, default: null },
    itemsProp: { type: Array, default: null },
    categoriesProp: { type: Array, default: null },
    addOnsProp: { type: Array, default: null },
    addOnGroupsProp: { type: Array, default: null }
  },
  emits: ['saved'],
  data() {
    return {
      loading: true,

      addOnTemplateId: "",
      items: [],
      categories: [],
      usedInAddOnGroupNames: [],

      price: 0,               //Takeaway price without VAT
      priceWithVat: "",       //Takeaway price with VAT
      priceDineIn: "",        //Dine-in price without VAT
      priceDineInWithVat: "", //Dine-in price with VAT

      addOnTemplate: {
        type: "add-on_template",
        merchant: "",
        name: "",
        price: 0,
        priceDineIn: 0,
        itemId: ""
      }
    };
  },
  mounted() {
    this.addOnTemplateId = this.addOnTemplateIdProp || this.$route.query.addOnTemplateId;

    if(this.addOnTemplateProp && this.itemsProp && this.categoriesProp && this.addOnsProp && this.addOnGroupsProp){
      //Data already loaded by the products page, no requests needed
      let addOnsOfTemplate = this.addOnsProp.filter(addOn => addOn.templateId === this.addOnTemplateId);
      this.initialize(Object.assign({}, this.addOnTemplateProp), this.itemsProp, this.categoriesProp, addOnsOfTemplate, this.addOnGroupsProp);
      return;
    }

    Promise.all([
      ProductService.getAddOnTemplates(),
      ProductService.getItems(),
      ProductService.getCategories(),
      ProductService.getAddOnsOfTemplate(this.addOnTemplateId),
      ProductService.getAddOnGroups()
    ]).then(
        ([templatesResponse, itemsResponse, categoriesResponse, addOnsResponse, addOnGroupsResponse]) => {
          let addOnTemplate = templatesResponse.data.find(template => template.id === this.addOnTemplateId);
          this.initialize(addOnTemplate || this.addOnTemplate, itemsResponse.data, categoriesResponse.data, addOnsResponse.data, addOnGroupsResponse.data);
        },
        (error) => {
          console.log(error);
        }
    );
  },
  computed: {
    useFunctionality(){
      return MerchantUtil.useFunctionality();
    },
    activateSaveButton(){
      if(this.addOnTemplate.name.length > 0){
        return true;
      } else {
        return false;
      }
    },
    isLinkedToItem(){
      return this.addOnTemplate.itemId.length > 0;
    },
    dineInPriceSuggestion(){
      //Suggest the same base price as takeaway, but only when the dine-in price has not been filled in yet
      if(this.isLinkedToItem || this.price === "" || Number(this.price) <= 0){
        return null;
      }
      if(Number(this.priceDineIn) > 0){
        return null; //Already filled in
      }
      return Number(this.price);
    },
    dineInPriceSuggestionWithVat(){
      return ItemUtil.getPriceDineIn(this.dineInPriceSuggestion || 0, this.vat);
    }
  },
  methods: {
    initialize(addOnTemplate, items, categories, addOnsOfTemplate, addOnGroups){
      this.addOnTemplate = addOnTemplate;
      if(!this.addOnTemplate.itemId){
        this.addOnTemplate.itemId = "";
      }
      this.items = items;
      this.categories = categories;
      this.usedInAddOnGroupNames = this.getUsedInAddOnGroupNames(addOnsOfTemplate, addOnGroups);

      this.price = this.addOnTemplate.price;
      this.priceDineIn = this.addOnTemplate.priceDineIn;
      this.updatePriceWithVat();
      this.updatePriceDineInWithVat();

      this.loading = false;
    },
    getUsedInAddOnGroupNames(addOnsOfTemplate, addOnGroups){
      let addOnGroupNames = [];
      for (let i = 0; i < addOnsOfTemplate.length; i++) {
        let addOnGroup = addOnGroups.find(group => group.id === addOnsOfTemplate[i].addOnGroupId);
        if(addOnGroup && !addOnGroupNames.includes(addOnGroup.name)){
          addOnGroupNames.push(addOnGroup.name);
        }
      }
      return addOnGroupNames;
    },
    onItemSelected(){
      if(this.isLinkedToItem){
        let item = this.items.find(item => item.id === this.addOnTemplate.itemId);
        if(item){
          this.addOnTemplate.name = item.name;
          this.price = item.price;
          this.priceDineIn = item.priceDineIn;
          this.updatePriceWithVat();
          this.updatePriceDineInWithVat();
        }
      }
    },
    updatePriceWithVat(){
      let priceWithVat = this.price + (this.price / 100 * this.vat.takeOut);
      this.priceWithVat = Math.round(priceWithVat);
    },
    updatePriceDineInWithVat(){
      let priceDineInWithVat = this.priceDineIn + (this.priceDineIn / 100 * this.vat.dineIn);
      this.priceDineInWithVat = Math.round(priceDineInWithVat);
    },
    updatePrice(){
      this.price = ItemUtil.getPriceWithoutVat(this.vat.takeOut, this.priceWithVat);
    },
    updatePriceDineIn(){
      this.priceDineIn = ItemUtil.getPriceWithoutVat(this.vat.dineIn, this.priceDineInWithVat);
    },
    applyDineInPriceSuggestion(){
      this.priceDineIn = Number(this.price);
      this.updatePriceDineInWithVat();
    },
    saveAddOnTemplate(){
      this.loading = true;

      this.addOnTemplate.price = this.price;
      this.addOnTemplate.priceDineIn = this.priceDineIn;

      let addOnTemplate = Object.assign({}, this.addOnTemplate);
      addOnTemplate.addOnTemplateId = this.addOnTemplateId;
      delete addOnTemplate.id;
      console.log('addOnTemplate: ' + JSON.stringify(addOnTemplate));
      ProductService.editAddOnTemplate(addOnTemplate).then(
          (response) => {
            let documentId = response.data;
            if(documentId){
              if(this.panelMode){ this.$emit('saved'); } else { this.$router.push({ path: 'products'}); }
            } else {
              console.log("Editing addOnTemplate failed");
            }
            this.loading = false;
          },
          (error) => {
            console.log(error);
          }
      );
    }
  }
};
</script>
